import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { calcTimeLeft, pad, pluralizeDays, pluralizeLabel } from '../utils/timer';
import { getUnviewedPrizes } from '../utils/viewedPrizes';
import type { ClaimedPrize, DropCurrentResponse, TierData } from '../types';
import { PenkiBadge } from './PenkiBadge';
import './MyDropsPage.css';

type TabType = 'drops' | 'claimed';

function formatPrizeQuantityLabel(n: number): string {
  const qty = Math.max(0, Math.floor(n));
  const word = pluralizeLabel(qty, 'приз', 'приза', 'призов');
  return `${qty} ${word}`;
}

function dropTierToRarity(tier: number): 'common' | 'rare' | 'epic' | 'legendary' {
  switch (tier) {
    case 1: return 'legendary';
    case 2: return 'epic';
    case 3: return 'rare';
    case 4: return 'common';
    default: return 'common';
  }
}

export const MyDropsPage: React.FC = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<TabType>('drops');
  const [activeTooltip, setActiveTooltip] = useState<'howToWin' | 'dropDates' | null>(null);
  const howToWinBtnRef = useRef<HTMLButtonElement>(null);
  const dropDatesBtnRef = useRef<HTMLButtonElement>(null);

  const toggleTooltip = (id: 'howToWin' | 'dropDates') => {
    setActiveTooltip(prev => prev === id ? null : id);
  };

  // Drop data from API
  const [dropData, setDropData] = useState<DropCurrentResponse | null>(null);
  const [dropLoading, setDropLoading] = useState(true);

  // Absolute target timestamps computed once after API fetch
  const [mainTimerTarget, setMainTimerTarget] = useState<number | null>(null);
  const [tierTimerTargets, setTierTimerTargets] = useState<Record<number, number>>({});

  // Tick counter to force re-render every second
  const [, setTick] = useState(0);

  // Claimed prizes state
  const [claimedPrizes, setClaimedPrizes] = useState<ClaimedPrize[]>([]);
  const [claimedCount, setClaimedCount] = useState(0);
  const [claimedLoading, setClaimedLoading] = useState(false);
  const [claimedLoaded, setClaimedLoaded] = useState(false);
  const [highlightPrizeIds, setHighlightPrizeIds] = useState<Set<number>>(new Set());
  const [showNewPrizeAlert, setShowNewPrizeAlert] = useState(false);
  const [alertFading, setAlertFading] = useState(false);
  const [highlightFading, setHighlightFading] = useState(false);

  // Fetch drop data
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await apiClient.getCurrentDrop();
        if (cancelled) return;
        setDropData(data);

        const now = Date.now();
        if (data.next_scheduled_drop) {
          setMainTimerTarget(now + data.next_scheduled_drop.time_remaining * 1000);
        }

        const targets: Record<number, number> = {};
        for (const tier of data.tiers) {
          if (tier.drop?.status === 'scheduled' && tier.drop.time_remaining > 0) {
            targets[tier.tier] = now + tier.drop.time_remaining * 1000;
          }
        }
        setTierTimerTargets(targets);
      } catch {
        // ignore
      } finally {
        if (!cancelled) setDropLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Global 1-second tick for countdowns
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Claimed prizes (lazy)
  const fetchClaimedPrizes = useCallback(async () => {
    setClaimedLoading(true);
    try {
      const result = await apiClient.getClaimedPrizes();
      setClaimedPrizes(result.data);
      setClaimedCount(result.total);
      setClaimedLoaded(true);

      const unviewed = getUnviewedPrizes(result.data);
      if (unviewed.length > 0) {
        setHighlightPrizeIds(new Set(unviewed.map(p => p.id)));
        setShowNewPrizeAlert(true);
      }
    } catch {
      // ignore
    } finally {
      setClaimedLoading(false);
    }
  }, []);

  // Загружаем claimed prizes при монтировании (для счётчика на вкладке)
  useEffect(() => {
    if (!claimedLoaded) {
      fetchClaimedPrizes();
    }
  }, [claimedLoaded, fetchClaimedPrizes]);

  useEffect(() => {
    if (!showNewPrizeAlert) return;
    const fadeTimer = setTimeout(() => setAlertFading(true), 5000);
    const removeTimer = setTimeout(() => {
      setShowNewPrizeAlert(false);
      setAlertFading(false);
    }, 5500);
    return () => { clearTimeout(fadeTimer); clearTimeout(removeTimer); };
  }, [showNewPrizeAlert]);

  useEffect(() => {
    if (highlightPrizeIds.size === 0) return;
    const fadeTimer = setTimeout(() => setHighlightFading(true), 5000);
    const removeTimer = setTimeout(() => {
      setHighlightPrizeIds(new Set());
      setHighlightFading(false);
    }, 5500);
    return () => { clearTimeout(fadeTimer); clearTimeout(removeTimer); };
  }, [highlightPrizeIds.size]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handlePrizeClick = (prize: ClaimedPrize) => navigate(`/prizes/${prize.id}`);

  const mainTime = mainTimerTarget ? calcTimeLeft(mainTimerTarget) : null;

  const getTierTimeStr = (tier: TierData): string => {
    const target = tierTimerTargets[tier.tier];
    if (!target) return '00:00:00';
    const t = calcTimeLeft(target);
    const hms = `${pad(t.hours)}:${pad(t.minutes)}:${pad(t.seconds)}`;
    if (t.days > 0) return `${pluralizeDays(t.days)} ${hms}`;
    return hms;
  };

  const renderTierStatusChip = (tier: TierData) => {
    if (!tier.drop) return 'Скоро запуск';
    if (tier.drop.status === 'active') return 'Дроп запущен';
    return `До дропа ${getTierTimeStr(tier)}`;
  };

  // Render: claimed prize card
  const renderClaimedPrizeCard = (item: ClaimedPrize) => {
    const isHighlighted = highlightPrizeIds.has(item.id);
    const cardClass = [
      'claimed-prize-card',
      isHighlighted ? 'highlight' : '',
      isHighlighted && highlightFading ? 'highlight-fading' : '',
    ].filter(Boolean).join(' ');
    return (
      <div key={item.id} className={cardClass} onClick={() => handlePrizeClick(item)}>
        {isHighlighted && <span className="claimed-prize-new-chip">Новый приз</span>}
        <div className="claimed-prize-image">
          {item.prize?.image_url ? (
            <img src={item.prize.image_url} alt={item.prize?.title || 'Приз'} className="claimed-prize-img" />
          ) : (
            <span className="claimed-prize-placeholder">🏆</span>
          )}
        </div>
        <div className="claimed-prize-info">
          <h4 className="claimed-prize-title">{item.prize?.title || 'Приз'}</h4>
          {item.claimed_at && (
            <p className="claimed-prize-date">Получен {formatDate(item.claimed_at)}</p>
          )}
        </div>
        <div className="claimed-prize-arrow">›</div>
      </div>
    );
  };

  return (
    <div className="drops-page">
      <PenkiBadge />

      {/* Tab Switcher (sticky) */}
      <div className="drops-tabs-sticky">
        <div className="drops-tabs">
          <button
            className={`drops-tab ${activeTab === 'drops' ? 'active' : ''}`}
            onClick={() => setActiveTab('drops')}
          >
            Дропы
          </button>
          <button
            className={`drops-tab ${activeTab === 'claimed' ? 'active' : ''}`}
            onClick={() => setActiveTab('claimed')}
          >
            Получено призов <span className="tab-count">{claimedCount}</span>
          </button>
        </div>
      </div>

      {/* DROPS TAB */}
      {activeTab === 'drops' && (
        <>
          {dropLoading ? (
            <div className="drops-loading">
              <div className="drops-spinner" />
            </div>
          ) : dropData && (
            <>
              {/* Header: title + chance badge */}
              <div className="drops-header">
                <h2 className="drops-header-title">Ваш шанс на приз</h2>
                {dropData.has_participating_drops && (
                  <span className="drops-chance-badge">{dropData.max_chance}%</span>
                )}
              </div>

              {/* Action buttons */}
              <div className="drops-action-buttons">
                <div className="drops-action-wrap">
                  <button ref={howToWinBtnRef} className="drops-action-btn" onClick={() => toggleTooltip('howToWin')}>
                    <span className="action-btn-icon"><img src="/question.svg" alt="" /></span>
                    Как выиграть?
                  </button>
                </div>
                <div className="drops-action-wrap">
                  <button ref={dropDatesBtnRef} className="drops-action-btn" onClick={() => toggleTooltip('dropDates')}>
                    <span className="action-btn-icon"><img src="/calendar.svg" alt="" /></span>
                    Даты дропов
                  </button>
                </div>
              </div>

              {/* Tooltip overlay */}
              {activeTooltip && (() => {
                const btn = activeTooltip === 'howToWin' ? howToWinBtnRef.current : dropDatesBtnRef.current;
                const r = btn?.getBoundingClientRect();
                const tooltipTop = r ? r.bottom + 10 : 0;
                const tooltipWidth = Math.min(280, window.innerWidth - 32);
                const tooltipLeft = r ? (window.innerWidth - tooltipWidth) / 2 : 0;
                const arrowLeft = r ? (r.left + r.width / 2) - tooltipLeft : 0;
                return createPortal(
                  <div
                    className="drops-tooltip-overlay"
                    onPointerDown={e => {
                      if (e.target !== e.currentTarget) return;
                      e.preventDefault();
                      setActiveTooltip(null);
                    }}
                  >
                    <div
                      className="drops-tooltip drops-tooltip--below"
                      style={r ? { top: tooltipTop } : {}}
                      onPointerDown={e => e.stopPropagation()}
                    >
                      <div
                        className="drops-tooltip-arrow drops-tooltip-arrow--up"
                        style={r ? { left: arrowLeft } : {}}
                      />
                      <p>
                        {activeTooltip === 'howToWin'
                          ? 'Чтобы выиграть приз, соберите как можно больше NFT нужного тира, так как призы начисляются случайным образом. Чем больше у вас NFT, тем выше шанс на победу'
                          : 'Даты заранее не определены. Но несколько раз в неделю мы запускаем таймер, по окончании которого начинается раздача призов'}
                      </p>
                    </div>
                  </div>,
                  document.body
                );
              })()}

              {/* Timer section */}
              {mainTime && mainTime.total > 0 ? (
                <div className="drops-timer-section">
                  <h3 className="timer-title">Новый дроп призов через:</h3>
                  <div className="countdown-box">
                      <div className="countdown-row">
                        {mainTime.days > 0 && (
                          <div className="countdown-days-wrap">
                            <div className="time-block">
                              <span className="time-value">{mainTime.days}</span>
                              <span className="time-label">
                                {pluralizeLabel(mainTime.days, 'день', 'дня', 'дней')}
                              </span>
                            </div>
                          </div>
                        )}
                        <div className="time-block">
                          <span className="time-value">{pad(mainTime.hours)}</span>
                          <span className="time-label">
                            {pluralizeLabel(mainTime.hours, 'час', 'часа', 'часов')}
                          </span>
                        </div>
                        <span className="time-sep">:</span>
                        <div className="time-block">
                          <span className="time-value">{pad(mainTime.minutes)}</span>
                          <span className="time-label">
                            {pluralizeLabel(mainTime.minutes, 'минута', 'минуты', 'минут')}
                          </span>
                        </div>
                        <span className="time-sep">:</span>
                        <div className="time-block">
                          <span className="time-value">{pad(mainTime.seconds)}</span>
                          <span className="time-label">
                            {pluralizeLabel(mainTime.seconds, 'секунда', 'секунды', 'секунд')}
                          </span>
                        </div>
                      </div>
                    </div>
                  <p className="timer-note">Призы распределяются автоматически</p>
                </div>
              ) : (
                <div className="drops-timer-waiting-card">
                  <img src="/timer-error.png" alt="" className="drops-timer-waiting-icon" />
                  <div className="drops-timer-waiting-text">
                    <h3 className="drops-timer-waiting-title">Скоро запустится таймер призов!</h3>
                    <p className="drops-timer-waiting-note">Успейте купить ху#ки и увеличить шанс на приз</p>
                  </div>
                </div>
              )}

              {/* Promo code — закомментировано */}
              {/*
              <div className="promo-code-section">
                <h3 className="promo-title">Введите промокод и получите приз!</h3>
                <form className="promo-form" onSubmit={handlePromoSubmit}>
                  <input
                    type="text"
                    className="promo-input"
                    placeholder="Промокод"
                    value={promoCode}
                    onChange={e => setPromoCode(e.target.value.toUpperCase())}
                    disabled={promoLoading}
                    maxLength={50}
                  />
                  <button
                    type="submit"
                    className="promo-btn button--accent button--accent-s"
                    disabled={!promoCode.trim() || promoLoading}
                  >
                    {promoLoading ? '...' : 'Забрать приз'}
                  </button>
                </form>
                {promoError && <div className="promo-error">{promoError}</div>}
                {promoSuccess && <div className="promo-success">{promoSuccess}</div>}
              </div>
              */}

              {/* Tier cards */}
              {dropData.tiers.map(tier => (
                <div
                  key={tier.tier}
                  className={`tier-drop-card${!tier.drop ? ' tier-drop-card--waiting' : ''}`}
                  style={{ background: tier.gradient }}
                >
                  <div className="tier-drop-header">
                    <h3 className="tier-drop-title">{tier.name}</h3>
                    <span className="tier-stat-badge tier-status-chip">
                      {renderTierStatusChip(tier)}
                    </span>
                  </div>

                  {/* Prize images row — показываем для запланированных и активных дропов */}
                  {tier.drop && (tier.drop.status === 'scheduled' || tier.drop.status === 'active') && tier.drop.prizes.length > 0 && (
                    <div className="tier-prizes-scroll-wrap">
                      <div className="tier-prizes-scroll">
                        {tier.drop.prizes.map(prize => (
                          prize.image_url && (
                            <div key={prize.id} className="tier-prize-wrap">
                              <div className="tier-prize-img-area">
                                <img
                                  src={prize.image_url}
                                  alt={prize.title}
                                  className="tier-prize-img"
                                />
                              </div>
                              <div className="tier-prize-count-badge">
                                {formatPrizeQuantityLabel(prize.quantity ?? 1)}
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Stats or buy prompt — не показываем кнопку для тиров "Скоро запуск" */}
                  {tier.drop && (
                    tier.user_nft_count > 0 ? (
                      <>
                        <div className="tier-stats-row">
                          <span className="tier-stat-badge">Шанс приза: {tier.chance}%</span>
                          <span className="tier-stat-badge">Кол-во ху#ков: {tier.user_nft_count}</span>
                        </div>
                        <div className="tier-buy-section" style={{ marginTop: 10 }}>
                          <button
                            type="button"
                            className="button button--primary button--primary-s"
                            onClick={() => {
                              const rarity = dropTierToRarity(tier.tier);
                              navigate(`/?rarity=${rarity}`);
                            }}
                          >
                            Купить ещё
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="tier-buy-section">
                        <p className="tier-buy-text">
                          Чтобы участвовать в розыгрыше, нужны ху#ки
                        </p>
                        <button
                          type="button"
                          className="button button--primary button--primary-s"
                          onClick={() => {
                            const rarity = dropTierToRarity(tier.tier);
                            navigate(`/?rarity=${rarity}`);
                          }}
                        >
                          Купить
                        </button>
                      </div>
                    )
                  )}
                </div>
              ))}
            </>
          )}
        </>
      )}

      {/* CLAIMED PRIZES TAB */}
      {activeTab === 'claimed' && (
        <div className="claimed-prizes-tab">
          {claimedLoading ? (
            <div className="drops-loading">
              <div className="drops-spinner" />
            </div>
          ) : claimedPrizes.length > 0 ? (
            <div className="claimed-prizes-list">
              {claimedPrizes.map(item => renderClaimedPrizeCard(item))}
            </div>
          ) : (
            <div className="claimed-prizes-empty">
              <h3 className="claimed-prizes-empty-title">Пока нет призов</h3>
              <p className="claimed-prizes-empty-text">
                Когда вы получите приз за NFT, он появится здесь
              </p>
            </div>
          )}

          {showNewPrizeAlert && (
            <div className={`new-prize-added-alert ${alertFading ? 'fading' : ''}`}>
              <img src="/success-s.png" alt="" className="new-prize-added-alert-icon" />
              <span className="new-prize-added-alert-text">Новый приз добавлен в список</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
